"use client";
import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import NavSearch from "./NavSearch";
import { getMerchantHref, getEventsHref, getPromotionHref } from "@/constants/hooks";
import { Menu, X, ChevronRight, ArrowRight } from "lucide-react";

const MobileNavMenu = ({
    company_id, mer_slug, slug_type, cat_slug,
    categories, merchantData, events, promotions,
    promo_slug, blog_url, blog_title, companyLogo, companyDomain,
    headerPromoMerchant,
}: any) => {
    const [open, setOpen]         = useState(false);
    const [expanded, setExpanded] = useState<string | null>(null);
    const pathname                = usePathname();
    const drawerRef               = useRef<HTMLDivElement>(null);

    /* lock body scroll */
    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    /* close on route change */
    useEffect(() => { setOpen(false); setExpanded(null); }, [pathname]);

    /* close on outside click */
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const close = () => { setOpen(false); setExpanded(null); };

    const navGroups = [
        { id: "stores",     label: "Stores",     path: "/all-stores/A",  items: merchantData || [], type: "mer"   },
        { id: "categories", label: "Categories", path: `/${cat_slug}`,   items: categories  || [], type: "cat"   },
        { id: "products",   label: "Products",   path: "/all-products",  items: [],                type: "prod"  },
        ...(events?.length     > 0 ? [{ id: "events",     label: "Events",     path: "/events",           items: events,     type: "event" }] : []),
        ...(promotions?.length > 0 ? [{ id: "promotions", label: "Promotions", path: `/${promo_slug}`,    items: promotions, type: "promo" }] : []),
    ];

    const getHref = (type: string, item: any) => {
        if (type === "cat")   return `/${item.url}`;
        if (type === "mer")   return getMerchantHref(item, mer_slug, slug_type);
        if (type === "event") return getEventsHref(item, slug_type);
        if (type === "promo") return getPromotionHref(item, promo_slug);
        return "/all-products";
    };

    const isActive = (path: string) =>
        path === "/" ? pathname === "/" : pathname === path || pathname.startsWith(path + "/");

    return (
        <div className="lg:hidden">

            {/* ── Burger button ── */}
            <button
                onClick={() => setOpen(true)}
                aria-label="Open menu"
                className="w-10 h-10 flex items-center justify-center rounded-xl
                    bg-slate-100 hover:bg-slate-200 text-slate-700
                    active:scale-90 transition-all"
            >
                <Menu size={20} strokeWidth={2} />
            </button>

            {/* ── Backdrop ── */}
            <div
                onClick={close}
                className={`fixed inset-0 z-[9990] bg-slate-900/60 backdrop-blur-sm transition-opacity duration-300
                    ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
            />

            {/* ── Drawer ── */}
            <div
                ref={drawerRef}
                className={`fixed top-0 left-0 h-[100dvh] w-[85vw] max-w-[340px] z-[9999]
                    bg-white flex flex-col shadow-[20px_0_60px_rgba(0,0,0,0.15)]
                    transition-transform duration-300 ease-in-out
                    ${open ? "translate-x-0" : "-translate-x-full"}`}
            >
                {/* top accent line — Theme-8 pink+blue */}
                <div className="w-full h-[3px] bg-gradient-to-r from-[#D61C4E] via-blue-600 to-[#D61C4E] shrink-0" />

                {/* ── Drawer header ── */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
                    <Link href="/" onClick={close}>
                        {companyLogo ? (
                            <Image
                                src={companyLogo}
                                alt="Logo"
                                width={120}
                                height={36}
                                className="h-8 w-auto object-contain"
                                priority
                            />
                        ) : (
                            <span className="text-xl font-black text-blue-600 italic tracking-tighter">MENU</span>
                        )}
                    </Link>
                    <button
                        onClick={close}
                        className="w-9 h-9 flex items-center justify-center rounded-xl
                            bg-slate-100 hover:bg-red-50 hover:text-red-500
                            text-slate-500 active:scale-90 transition-all"
                    >
                        <X size={16} strokeWidth={2.5} />
                    </button>
                </div>

                {/* ── Search ── */}
                <div className="px-4 pt-4 pb-2 shrink-0">
                    <NavSearch
                        companyId={company_id}
                        mer_slug={mer_slug}
                        slug_type={slug_type}
                        cat_slug={cat_slug}
                        domain={companyDomain}
                    />
                </div>

                {/* ── Nav items ── */}
                <nav
                    className="flex-1 overflow-y-auto px-3 py-3"
                    style={{ scrollbarWidth: "none" }}
                >
                    {/* Home */}
                    <Link
                        href="/"
                        onClick={close}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl no-underline mb-1 transition-colors
                            ${isActive("/")
                                ? "bg-blue-50 text-blue-600 font-black"
                                : "text-slate-700 hover:text-slate-900 hover:bg-slate-50 font-semibold"}`}
                    >
                        <span className="text-[15px]">Home</span>
                        {isActive("/") && (
                            <span className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-600" />
                        )}
                    </Link>

                    {/* Nav groups */}
                    {navGroups.map((group) => (
                        <div key={group.id} className="mb-1">
                            <button
                                onClick={() => {
                                    if (group.items.length === 0) {
                                        close();
                                        window.location.href = group.path;
                                        return;
                                    }
                                    setExpanded(expanded === group.id ? null : group.id);
                                }}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-left
                                    ${expanded === group.id || isActive(group.path)
                                        ? "bg-blue-50 text-blue-600"
                                        : "text-slate-700 hover:text-slate-900 hover:bg-slate-50"}`}
                            >
                                <span className="text-[15px] font-semibold flex-1">{group.label}</span>
                                {group.items.length > 0 ? (
                                    <ChevronRight
                                        size={14} strokeWidth={2.5}
                                        className={`text-slate-400 transition-transform duration-200
                                            ${expanded === group.id ? "rotate-90 text-blue-600" : ""}`}
                                    />
                                ) : (
                                    <ArrowRight size={14} strokeWidth={2.5} className="text-slate-300" />
                                )}
                            </button>

                            {/* Sub-items */}
                            {expanded === group.id && group.items.length > 0 && (
                                <div className="mt-1 ml-2 pl-3 border-l-2 border-slate-200 space-y-0.5 pb-2">
                                    {group.items.slice(0, 10).map((item: any, i: number) => (
                                        <Link
                                            key={i}
                                            href={getHref(group.type, item)}
                                            onClick={close}
                                            className="flex items-center gap-2 px-3 py-2.5 rounded-lg no-underline
                                                text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors"
                                        >
                                            <span className="w-1 h-1 rounded-full bg-slate-300 shrink-0" />
                                            <span className="text-[13px] font-medium truncate">
                                                {item.name || item.merchant_name}
                                            </span>
                                        </Link>
                                    ))}
                                    <Link
                                        href={group.path}
                                        onClick={close}
                                        className="flex items-center gap-1.5 px-3 py-2 no-underline
                                            text-blue-600 hover:text-blue-700 text-[11px] font-black uppercase tracking-widest transition-colors"
                                    >
                                        View all <ArrowRight size={11} strokeWidth={2.5} />
                                    </Link>
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Blog */}
                    {blog_url && (
                        <Link
                            href={blog_url}
                            target="_blank"
                            onClick={close}
                            className="flex items-center gap-3 px-4 py-3 rounded-xl no-underline
                                text-slate-700 hover:text-slate-900 hover:bg-slate-50
                                font-semibold transition-colors mb-1"
                        >
                            <span className="text-[15px]">{blog_title || "Blog"}</span>
                            <ArrowRight size={13} strokeWidth={2} className="ml-auto text-slate-300" />
                        </Link>
                    )}
                </nav>

                {/* ── Footer ── */}
                <div className="px-5 py-4 bg-slate-900 shrink-0 text-center">
                    <p className="text-[9px] text-slate-500 font-black tracking-[0.2em] uppercase mb-1.5">
                        © {new Date().getFullYear()} All Rights Reserved
                    </p>
                    <div className="flex justify-center gap-4">
                        <Link href="/privacy-policy" onClick={close}
                            className="no-underline text-[9px] text-slate-500 hover:text-slate-300 font-bold uppercase tracking-wider transition-colors">
                            Privacy
                        </Link>
                        <Link href="/terms-of-services" onClick={close}
                            className="no-underline text-[9px] text-slate-500 hover:text-slate-300 font-bold uppercase tracking-wider transition-colors">
                            Terms
                        </Link>
                        <Link href="/contact-us" onClick={close}
                            className="no-underline text-[9px] text-slate-500 hover:text-slate-300 font-bold uppercase tracking-wider transition-colors">
                            Contact
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MobileNavMenu;
